import { z } from "zod";

/**
 * Big Arena Competition registrations: the `/100k` whitelist and `/5k` sign-up
 * pages on Cede Arena, and the Solo CMS page that reviews requests and mints
 * KOL priority codes.
 *
 * A registration is one row per hub user and competition. Email comes from
 * Privy, wallet and socials from the account the user linked, so the client
 * only ever sends what the server cannot know: the competition, the priority
 * code and the landing utm.
 */

/**
 * BIG_100K is the whitelist: priority codes, referrals, and ACCEPTED rows gate
 * the tournament. TOURNAMENT_5K is a sign-up list with none of those.
 */
export const BIG_ARENA_COMPETITIONS = ["BIG_100K", "TOURNAMENT_5K"] as const;
export const BigArenaCompetitionSchema = z.enum(BIG_ARENA_COMPETITIONS);
export type BigArenaCompetition = z.infer<typeof BigArenaCompetitionSchema>;

export const BIG_ARENA_COMPETITION_REGISTRATION_STATUSES = ["PENDING", "ACCEPTED", "REJECTED"] as const;
export const BigArenaCompetitionRegistrationStatusSchema = z.enum(BIG_ARENA_COMPETITION_REGISTRATION_STATUSES);
export type BigArenaCompetitionRegistrationStatus = z.infer<typeof BigArenaCompetitionRegistrationStatusSchema>;

/** How an ACCEPTED row got there. Null while PENDING or REJECTED. */
export const BIG_ARENA_COMPETITION_ACCEPTED_VIA = ["PRIORITY_CODE", "MANUAL"] as const;
export const BigArenaCompetitionAcceptedViaSchema = z.enum(BIG_ARENA_COMPETITION_ACCEPTED_VIA);
export type BigArenaCompetitionAcceptedVia = z.infer<typeof BigArenaCompetitionAcceptedViaSchema>;

/**
 * Priority code shape: `BAC-XXXX-XXXX` over an alphabet without 0/O/1/I, so a
 * code read off a KOL's screenshot cannot be mistyped into a different one.
 * Redeem is case-insensitive; the stored form is uppercase.
 */
export const BIG_ARENA_COMPETITION_PRIORITY_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const BIG_ARENA_COMPETITION_PRIORITY_CODE_PREFIX = "BAC";
export const BIG_ARENA_COMPETITION_PRIORITY_CODE_REGEX = /^BAC-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;
export const BIG_ARENA_COMPETITION_PRIORITY_CODE_LABEL_MAX = 80;
export const BIG_ARENA_COMPETITION_PRIORITY_CODE_BATCH_MAX = 200;
/** Longest `search=` the CMS list accepts; the search box bounds itself to the same value. */
export const BIG_ARENA_COMPETITION_PRIORITY_CODE_SEARCH_MAX = 120;

/** Normalizes user input to the stored form: trimmed, uppercase, hyphens re-inserted if dropped. */
export const normalizeBigArenaCompetitionPriorityCode = (raw: string): string => {
  const compact = raw.trim().toUpperCase().replace(/[\s-]/g, "");
  if (compact.length !== 11 || !compact.startsWith(BIG_ARENA_COMPETITION_PRIORITY_CODE_PREFIX))
    return raw.trim().toUpperCase();
  return `${compact.slice(0, 3)}-${compact.slice(3, 7)}-${compact.slice(7, 11)}`;
};

export const BigArenaCompetitionPriorityCodeSchema = z
  .string()
  .transform(normalizeBigArenaCompetitionPriorityCode)
  .pipe(z.string().regex(BIG_ARENA_COMPETITION_PRIORITY_CODE_REGEX, { error: "not a valid priority code" }));

/**
 * The bare `?utm=` a visitor landed with. Same bound as the Mixpanel people
 * schema so a stored value can never 400 a later request.
 */
export const BIG_ARENA_COMPETITION_UTM_MAX = 100;
const blankToUndefined = (value: string | undefined): string | undefined =>
  value === undefined || value === "" ? undefined : value;

/** A registrant's share tag starts with this; the rest is lowercase, so the tag is matched exactly. */
export const BIG_ARENA_COMPETITION_REFERRAL_UTM_PREFIX = "bac_";
export const isBigArenaCompetitionReferralUtm = (utm: string): boolean =>
  utm.toLowerCase().startsWith(BIG_ARENA_COMPETITION_REFERRAL_UTM_PREFIX);

/** A hand-retyped `?utm=BAC_XXXX` still credits the referrer; other tags are kept as typed. */
const normalizeReferralUtm = (value: string | undefined): string | undefined =>
  value !== undefined && isBigArenaCompetitionReferralUtm(value) ? value.toLowerCase() : value;

/**
 * `/100k` tabs still running a bundle from before `competition` existed send
 * none. Drop the default once that arena release has been live for a while.
 */
const LegacyClientCompetitionSchema = BigArenaCompetitionSchema.default("BIG_100K");

/** Body for `POST /v1/big-arena-competition/registration`. Authenticated. */
export const SubmitBigArenaCompetitionRegistrationRequestSchema = z
  .object({
    competition: LegacyClientCompetitionSchema,
    priorityCode: z
      .string()
      .trim()
      .max(32)
      .optional()
      .transform(blankToUndefined)
      .pipe(BigArenaCompetitionPriorityCodeSchema.optional()),
    utm: z
      .string()
      .trim()
      .max(BIG_ARENA_COMPETITION_UTM_MAX)
      .optional()
      .transform(blankToUndefined)
      .transform(normalizeReferralUtm),
  })
  .refine((body) => body.competition === "BIG_100K" || body.priorityCode === undefined, {
    path: ["priorityCode"],
    error: "priority codes only apply to the BIG_100K whitelist",
  });
export type SubmitBigArenaCompetitionRegistrationRequest = z.infer<
  typeof SubmitBigArenaCompetitionRegistrationRequestSchema
>;

/** Each friend who registers through the share link is one more chance to be picked, up to this many. */
export const BIG_ARENA_COMPETITION_REFERRAL_CAP = 5;

/** Query for `GET /v1/big-arena-competition/registration`. */
export const GetMyBigArenaCompetitionRegistrationQuerySchema = z.object({
  competition: LegacyClientCompetitionSchema,
});
export type GetMyBigArenaCompetitionRegistrationQuery = z.infer<typeof GetMyBigArenaCompetitionRegistrationQuerySchema>;

const MyRegistrationBaseSchema = z.object({
  status: BigArenaCompetitionRegistrationStatusSchema,
  acceptedVia: BigArenaCompetitionAcceptedViaSchema.nullable(),
  email: z.string(),
  defiAddress: z.string().nullable(),
  xHandle: z.string().nullable(),
  telegramHandle: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/** What the registrant sees about their own request. Only the whitelist has a referral programme. */
export const MyBigArenaCompetitionRegistrationDtoSchema = z.discriminatedUnion("competition", [
  MyRegistrationBaseSchema.extend({
    competition: z.literal("BIG_100K"),
    /** The registrant's own share tag: `/100k?utm=<referralUtm>` credits them for every registration it brings. */
    referralUtm: z.string(),
    /** Registrations that landed with `referralUtm` and were not rejected, uncapped; the page clamps to the cap. */
    referralCount: z.number().int().min(0),
  }),
  MyRegistrationBaseSchema.extend({ competition: z.literal("TOURNAMENT_5K") }),
]);
export type MyBigArenaCompetitionRegistrationDto = z.infer<typeof MyBigArenaCompetitionRegistrationDtoSchema>;
export type MyBigArenaCompetitionRegistrationOf<C extends BigArenaCompetition> = Extract<
  MyBigArenaCompetitionRegistrationDto,
  { competition: C }
>;

/** Response for `GET /v1/big-arena-competition/registration`. `registration` is null until the user submits once. */
export const GetMyBigArenaCompetitionRegistrationResponseSchema = z.object({
  registration: MyBigArenaCompetitionRegistrationDtoSchema.nullable(),
});
export type GetMyBigArenaCompetitionRegistrationResponse = z.infer<
  typeof GetMyBigArenaCompetitionRegistrationResponseSchema
>;

export const SubmitBigArenaCompetitionRegistrationResponseSchema = z.object({
  registration: MyBigArenaCompetitionRegistrationDtoSchema,
});
export type SubmitBigArenaCompetitionRegistrationResponse = z.infer<
  typeof SubmitBigArenaCompetitionRegistrationResponseSchema
>;

const RegistrationBaseSchema = z.object({
  id: z.number().int().positive(),
  hubUserId: z.number().int().positive(),
  email: z.string(),
  defiAddress: z.string().nullable(),
  xHandle: z.string().nullable(),
  telegramHandle: z.string().nullable(),
  status: BigArenaCompetitionRegistrationStatusSchema,
  acceptedVia: BigArenaCompetitionAcceptedViaSchema.nullable(),
  /** The code that auto-accepted this row, when there was one. */
  priorityCode: z
    .object({
      id: z.number().int().positive(),
      code: z.string(),
      label: z.string(),
    })
    .nullable(),
  /** The `?utm=` the registrant landed with. Another registrant's `referralUtm` when they were referred. */
  utm: z.string().nullable(),
  /** Hub user ids of other registrations for the same competition on the same X or Telegram account. */
  sameSocialAs: z.number().int().positive().array(),
  /** ISO instant of the last CMS decision; null while nobody has reviewed the row. */
  reviewedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/** One registration as the CMS sees it. */
export const BigArenaCompetitionRegistrationDtoSchema = z.discriminatedUnion("competition", [
  RegistrationBaseSchema.extend({
    competition: z.literal("BIG_100K"),
    referralUtm: z.string(),
    /** Registrations that landed with this row's `referralUtm` and were not rejected. */
    referralCount: z.number().int().nonnegative(),
  }),
  RegistrationBaseSchema.extend({ competition: z.literal("TOURNAMENT_5K") }),
]);
export type BigArenaCompetitionRegistrationDto = z.infer<typeof BigArenaCompetitionRegistrationDtoSchema>;

/** Paginated response for `GET /v1/cms/big-arena-competition/registrations/get-by-page`. Page is 0-indexed. */
export const ListBigArenaCompetitionRegistrationsResponseSchema = z.object({
  items: BigArenaCompetitionRegistrationDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListBigArenaCompetitionRegistrationsResponse = z.infer<
  typeof ListBigArenaCompetitionRegistrationsResponseSchema
>;

/** Body for `POST /v1/cms/big-arena-competition/registrations/review`. */
export const ReviewBigArenaCompetitionRegistrationBodySchema = z.object({
  id: z.number().int().positive(),
  status: BigArenaCompetitionRegistrationStatusSchema,
  /** The status the operator was looking at; the write is refused when the row has moved since. */
  expectedStatus: BigArenaCompetitionRegistrationStatusSchema,
});
export type ReviewBigArenaCompetitionRegistrationBody = z.infer<typeof ReviewBigArenaCompetitionRegistrationBodySchema>;

/** One KOL priority code as the CMS sees it. */
export const BigArenaCompetitionPriorityCodeDtoSchema = z.object({
  id: z.number().int().positive(),
  code: z.string(),
  /** Free text ops stamps on a batch, typically the KOL it was handed to. */
  label: z.string(),
  createdAt: z.string(),
  usedAt: z.string().nullable(),
  usedBy: z
    .object({
      registrationId: z.number().int().positive(),
      hubUserId: z.number().int().positive(),
      email: z.string(),
    })
    .nullable(),
  /** Set by ops to pull an unused code; a used code is never disabled. */
  disabledAt: z.string().nullable(),
});
export type BigArenaCompetitionPriorityCodeDto = z.infer<typeof BigArenaCompetitionPriorityCodeDtoSchema>;

export const ListBigArenaCompetitionPriorityCodesResponseSchema = z.object({
  items: BigArenaCompetitionPriorityCodeDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListBigArenaCompetitionPriorityCodesResponse = z.infer<
  typeof ListBigArenaCompetitionPriorityCodesResponseSchema
>;

const PriorityCodeLabelSchema = z.string().trim().min(1).max(BIG_ARENA_COMPETITION_PRIORITY_CODE_LABEL_MAX);

/** Body for `POST /v1/cms/big-arena-competition/priority-codes/generate`. */
export const GenerateBigArenaCompetitionPriorityCodesBodySchema = z.object({
  count: z.number().int().positive().max(BIG_ARENA_COMPETITION_PRIORITY_CODE_BATCH_MAX),
  label: PriorityCodeLabelSchema,
});
export type GenerateBigArenaCompetitionPriorityCodesBody = z.infer<
  typeof GenerateBigArenaCompetitionPriorityCodesBodySchema
>;

export const GenerateBigArenaCompetitionPriorityCodesResponseSchema = z.object({
  items: BigArenaCompetitionPriorityCodeDtoSchema.array(),
});
export type GenerateBigArenaCompetitionPriorityCodesResponse = z.infer<
  typeof GenerateBigArenaCompetitionPriorityCodesResponseSchema
>;

/** Body for `POST /v1/cms/big-arena-competition/priority-codes/update-label`. */
export const UpdateBigArenaCompetitionPriorityCodeLabelBodySchema = z.object({
  id: z.number().int().positive(),
  label: PriorityCodeLabelSchema,
});
export type UpdateBigArenaCompetitionPriorityCodeLabelBody = z.infer<
  typeof UpdateBigArenaCompetitionPriorityCodeLabelBodySchema
>;

/** Body for `POST /v1/cms/big-arena-competition/priority-codes/disable`. */
export const DisableBigArenaCompetitionPriorityCodeBodySchema = z.object({
  id: z.number().int().positive(),
});
export type DisableBigArenaCompetitionPriorityCodeBody = z.infer<
  typeof DisableBigArenaCompetitionPriorityCodeBodySchema
>;
